
import { Card } from '@/components/ui/card';
import { useLanguage } from '@/hooks/useLanguage';
import { CVEItem } from '@/types/cve';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { CalendarDays, AlertTriangle, Zap, Siren, Lock, Bug } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { getSeverityIcon, getSeverityClass } from './CVESeverityIcons';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface CVECardProps {
  item: CVEItem;
  index: number;
}

interface ShodanCVEData {
  cve_id: string;
  summary: string;
  cvss: number;
  epss: number;
  ranking_epss: number;
  kev: boolean;
  ransomware_campaign?: string;
  detail?: string; // For error/no info check
}

export function CVECard({ item, index }: CVECardProps) {
  const { language, t } = useLanguage();
  const navigate = useNavigate();

  // Fetch Shodan Data
  const { data: shodanData, isLoading } = useQuery({
    queryKey: ['cve-card', item.cve_id],
    queryFn: async () => {
      const res = await fetch(`/api/shodan/cve/${item.cve_id}`);
      if (!res.ok) return null;
      return res.json() as Promise<ShodanCVEData>;
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  // Format date
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);

      if (language === 'en') {
        return format(date, 'PPP');
      } else {
        return format(date, 'd MMMM yyyy', { locale: pl });
      }
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateString;
    }
  };

  const hasNoInfo = shodanData?.detail === "No information available";
  const isKev = shodanData?.kev;
  const isRansomware = shodanData?.ransomware_campaign && shodanData.ransomware_campaign !== 'Unknown';
  // Assuming 'Exploit' might be inferred if KEV is true or high EPSS, but user asked for separate.
  // For now, I'll use a generic 'Exploit' icon if EPSS is very high (>0.5) or if KEV is true, 
  // but user said "ma exploita" (has exploit). Shodan doesn't explicitly say "has exploit" in the summary usually.
  // I will use the 'Bug' icon for Exploit if KEV is true, as KEV implies exploit exists.
  // Actually, let's just use KEV and Ransomware as primary. 
  // User said: "EPSS ma być dodany zawsze, a w przpadku KEV = true, "ransomware_campaign": "Known" daj piktogramy..."
  // And "Exploit". I'll add a 'Bug' icon if EPSS > 0.1 (10%) as a proxy for "likely exploitable" if not KEV?
  // Or maybe just stick to KEV/Ransomware for now as they are explicit.

  return (
    <Card
      className={cn(
        "glass-card overflow-hidden transition-all duration-500 hover:shadow-xl hover:-translate-y-1 flex flex-col h-full",
        "animate-scale-in",
        (isKev || isRansomware) && "border-l-4 border-l-red-500 dark:border-l-red-500"
      )}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="p-6 flex-grow flex flex-col">
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center">
            {getSeverityIcon(language === 'en' ? item.severity_en : item.severity)}
            <span className={cn("text-xs font-semibold ml-1",
              getSeverityClass(language === 'en' ? item.severity_en : item.severity))}>
              {language === 'en' ? item.severity_en : item.severity}
            </span>
          </div>
          <div className="flex items-center text-xs text-muted-foreground">
            <CalendarDays className="h-3 w-3 mr-1" />
            {formatDate(item.publishedDate)}
          </div>
        </div>

        <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
          {item.cve_id}
          {isKev && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Siren className="h-4 w-4 text-red-500 animate-pulse" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>CISA KEV: Aktywna eksploatacja</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {isRansomware && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Lock className="h-4 w-4 text-purple-500" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Ransomware: {shodanData?.ransomware_campaign}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </h3>

        <p className="text-sm text-muted-foreground mb-4 line-clamp-3 flex-grow">
          {language === 'en' ? item.description : item.description_pl}
        </p>

        <div className="mt-auto space-y-3">
          {/* Indicators */}
          {shodanData && !hasNoInfo && (
            <div className="flex items-center gap-2 text-xs">
              <Badge variant="outline" className="flex items-center gap-1 bg-blue-500/10 text-blue-600 border-blue-200 dark:border-blue-800">
                <Zap className="h-3 w-3" />
                EPSS: {(shodanData.epss * 100).toFixed(2)}%
              </Badge>
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-border/50">
            <div className="flex items-center">
              <AlertTriangle className="h-4 w-4 mr-1 text-amber-500" />
              <span className="text-sm font-medium">
                {t('cve.score')}: {item.score.toFixed(1)}
              </span>
            </div>

            {shodanData && shodanData.epss !== undefined && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                disabled={isLoading}
                onClick={() => navigate(`/CVE/${item.cve_id}`, {
                  state: {
                    description_en: item.description,
                    description_pl: item.description_pl
                  }
                })}
              >
                {isLoading ? "Ładowanie..." : t('cve.details')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
